import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { groupsOf, type AccessGroup } from '@/api/auth';
import {
  DEFAULT_DASHBOARD_PERIOD,
  fetchDashboard,
  type Dashboard as DashboardPayload,
  type DashboardPeriod,
} from '@/api/dashboard';
import type { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { Table } from '@/components/ui/Table';
import { useSession } from '@/app/session';
import { useOutstandingColumns } from '@/pages/Outstanding/outstandingRow';
import { OUTSTANDING_PATH } from '@/pages/Outstanding/outstandingUrls';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { DashboardSkeleton } from './DashboardSkeleton';
import { FeedbackDistributionSection } from './FeedbackDistributionSection';
import { HealthSection } from './HealthSection';
import { InterviewerActivitySection } from './InterviewerActivitySection';
import { PeriodSelector } from './PeriodSelector';
import { PipelineSection } from './PipelineSection';
import { SetupPath } from './SetupPath';
import { canWalkSetupPath } from './setupSteps';

/**
 * The width of one aging bar, in twelfths, as a **static** Tailwind class.
 *
 * A bar's length is data, and Tailwind cannot compose a class it has never seen in the source — so
 * the twelve widths are written out here and one is chosen, rather than a percentage being written
 * into a `style` attribute. `.claude/rules/frontend.md`: Tailwind classes only, no inline styles.
 */
const BAR_WIDTHS = [
  'w-0',
  'w-1/12',
  'w-2/12',
  'w-3/12',
  'w-4/12',
  'w-5/12',
  'w-6/12',
  'w-7/12',
  'w-8/12',
  'w-9/12',
  'w-10/12',
  'w-11/12',
  'w-full',
] as const;

const barWidth = (count: number, largest: number): string =>
  BAR_WIDTHS[largest > 0 ? Math.round((count / largest) * 12) : 0];

/**
 * `DESIGN.md` §6.2 — the module's landing screen.
 *
 * **The sections a viewer sees are decided by the API, in the body** (ADR 0015): an Interviewer's
 * payload simply omits `health`, `pipeline` and `interviewer_activity`, a Recruiter's omits
 * `interviewer_activity`, and a section that is absent from the payload is **absent from the
 * screen** — no empty frame, no "you do not have access", and never a branch on the viewer's group
 * written here. The screen renders what it is given.
 *
 * **No draft content reaches this screen.** The API does not send any (ADR 0006) and nothing here
 * may display something that looks like it: a Section 1 row is a candidate, a vacancy, an
 * interviewer and an age, never a word of what was written.
 */
export default function Dashboard() {
  const session = useSession();

  /**
   * ⚠️ **No session, no screen** — and specifically no query key.
   *
   * A disabled query still *reads the cache at its key*, so a placeholder viewer id (this was
   * `?? 0`) is one shared bucket that every session-less render looks into and that anything could
   * be written to. The viewer is not optional in this key (ADR 0015), so the component that builds
   * it does not exist until there is a viewer. Until then this is §8's loading state, which is what
   * it was rendering anyway.
   */
  if (!session.data) return <DashboardSkeleton />;

  return <DashboardForViewer viewerId={session.data.user.id} groups={groupsOf(session.data)} />;
}

function DashboardForViewer({ viewerId, groups }: { viewerId: number; groups: AccessGroup[] }) {
  const { t } = useTranslation('dashboard');
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD);

  const dashboard = useQuery<DashboardPayload, ApiError>({
    // Viewer-keyed, because the same URL legitimately answers differently per caller (ADR 0015);
    // period-keyed, because two windows are two different answers and must not share an entry.
    queryKey: queryKeys.dashboard(viewerId, period),
    queryFn: () => fetchDashboard(period),
    /**
     * Switching the period keeps the sections on screen while the next window arrives, instead of
     * dropping the page back to skeletons (§8: never a layout that jumps).
     *
     * ⚠️ **Scoped to a period change, and never to a change of viewer.** `keepPreviousData` stood
     * here, and it resolves from the observer's last payload under *any* key: when the viewer
     * changes under a mounted Dashboard it hands the previous viewer's body to the next one, with
     * `status: 'success'`, so the skeleton branch below is skipped and the sections render — Section
     * 5 for someone who may never see it (ADR 0015 §1) and Section 4 blind-filtered for the wrong
     * caller (ADR 0006). The key carried the viewer; the placeholder walked around the key. So the
     * previous payload is offered only when it belonged to this same viewer.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === viewerId ? previous : undefined,
  });

  /**
   * §6.2 Section 1's four columns, described **once** for this section and for the screen *View all →*
   * leads to (`Outstanding/outstandingRow.tsx`). A reader who follows that link must meet the same
   * table, not a second description of it that agrees with this one today.
   */
  const columns = useOutstandingColumns();

  if (dashboard.isPending) {
    return <DashboardSkeleton />;
  }

  if (dashboard.isError) {
    /**
     * The shared mapping decides which §8 state a failure is — offline, forbidden or error — so
     * this screen states its sentence and nothing about status codes (`ApiErrorState`).
     *
     * ⚠️ **The page title stays.** This branch returned the state alone, so a failed dashboard was a
     * document with no `h1` anywhere: a screen-reader user landing on it heard one sentence and a
     * button, with nothing saying which screen had failed (§9, found in a live browser 2026-07-25).
     * A failure is still the dashboard, and every other screen in the app carries exactly one `h1`.
     */
    return (
      <>
        <h1 className={type.pageTitle}>{t('title')}</h1>
        <div className="mt-10">
          <ApiErrorState
            error={dashboard.error}
            message={t('error')}
            onRetry={() => void dashboard.refetch()}
          />
        </div>
      </>
    );
  }

  const {
    outstanding_feedback: outstanding,
    setup,
    health,
    pipeline,
    feedback_distribution: distribution,
    interviewer_activity: activity,
  } = dashboard.data;
  /**
   * ⚠️ §6.2: **"A brand-new tenant must not see a grid of zeros. Until there is at least one
   * submitted interview, the dashboard is replaced by a short setup path."**
   *
   * `has_submitted_interview` is the trigger, and it is the whole trigger — the backend added the
   * field for this path and says so (`dashboard/schemas.py`, `SetupState`: *"the dashboard-wide
   * empty-state signal §6.2's first-run path renders"*). What stood here branched on the three step
   * booleans instead and rendered the path **above** the dashboard, so a tenant with a vacancy, its
   * questions and a candidate but nothing submitted yet — exactly the first-run tenant §6.2 opens by
   * describing — saw the grid of zeros and no setup path at all.
   *
   * It replaces the dashboard **only for a viewer who can walk it**: an Interviewer may create
   * none of the three things and is shown Section 1 instead (`canWalkSetupPath`).
   */
  if (!setup.has_submitted_interview && canWalkSetupPath(groups)) {
    return (
      <>
        <h1 className={type.pageTitle}>{t('title')}</h1>
        <SetupPath setup={setup} groups={groups} />
      </>
    );
  }

  /**
   * The selector appears only when the payload carries something it scopes. An Interviewer gets no
   * period-scoped section at all, and a control that visibly changes nothing is a control that lies.
   *
   * ⚠️ **The subtitle renders under exactly this condition and disappears with it** (§6.2's *Header*,
   * TASK-149). *"Period affects analysis below"* promises an analysis, so a viewer who gets none — an
   * Interviewer, whose only section is Section 1 — would be told about a control they cannot see and
   * sections they do not have, which is worse than saying nothing.
   */
  const hasPeriodScopedSection = Boolean(health ?? distribution ?? activity);

  /** What a full-width aging bar means — the busiest band, never the total (§6.2's bars compare). */
  const largestBucket = Math.max(0, ...outstanding.buckets.map((bucket) => bucket.count));

  return (
    <>
      {/*
       * §6.2's *Header*: the title, the subtitle beneath it, and the period selector on the same row
       * at the content column's right edge — the reference's own composition, restored 2026-08-12.
       */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={type.pageTitle}>{t('title')}</h1>
          {/*
           * ⚠️ **Load-bearing, and not decoration** (§6.2's *Header*, TASK-149). With the *Analysis*
           * boundary reversed this sentence is the only thing on the screen stating how far the
           * selector reaches, so deleting it re-opens §6.2's own predicted misreading — *"someone will
           * change the period and believe the backlog shrank"*. What the boundary said with a rule, it
           * now says in words; that is the trade the reversal makes, stated rather than discovered.
           */}
          {hasPeriodScopedSection && <p className={cn(type.metadata, 'mt-1')}>{t('subtitle')}</p>}
        </div>
        {hasPeriodScopedSection && <PeriodSelector value={period} onChange={setPeriod} />}
      </div>

      {/* Every section is a labelled region, so the five of §6.2 are reachable one by one (§9). */}
      <section className="mt-10" aria-label={t('outstanding')}>
        {/* §6.2: the eyebrow carries the trailing count — *OUTSTANDING FEEDBACK 7*. */}
        <Eyebrow count={outstanding.total}>{t('outstanding')}</Eyebrow>

        {outstanding.total === 0 ? (
          /*
           * §6.2: "If it is zero, the whole section collapses to one line: *No outstanding
           * feedback.* in `positive` — that is a state worth celebrating quietly, and a grid of
           * zeros is not." One line, so no icon and no card: the shape of an `EmptyState` is the
           * shape of a problem. It is also a different state from "nothing set up yet" (§8), which
           * has replaced this whole screen by the time it could apply.
           */
          <p className="text-sm text-positive">{t('nothingOutstanding')}</p>
        ) : (
          /*
           * ⚠️ **Two columns, not one stack** (§6.2, 2026-08-12, from the reference): the hero count
           * and the aging bands on the left, the oldest-rows table taking the rest of the width in a
           * hairline-bordered block. It stacks at narrow widths (§10) — one column below `lg`, which is
           * the same breakpoint `Table` itself stacks at, so the table never becomes a label/value list
           * while it is still sharing a row with the bands.
           */
          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
            <div className="flex flex-wrap items-start gap-8">
              {/*
                §6.2: "The count in Display type (`font-mono text-5xl`)" — the hero metric of the
                landing screen, and `type.display` is the role for it: **a dashboard metric**, which
                is what that role means and all it means. It rendered as `Eyebrow`'s trailing count,
                at metadata size, beneath bucket counts twice its weight.

                ⚠️ **This comment quoted `type.display`'s own docstring — *"the only place a number
                is large"* — until 2026-08-11, and that claim is retracted** (TASK-118 decided it,
                TASK-119 made it false: §6.6 and §6.7 now render a per-question rating as a 30px
                margin mark). Nothing about *this* metric changed, and that is exactly why the line
                was a hazard: a retracted claim quoted as a justification reads as still true at the
                one site nobody re-derives. `DESIGN.md` §3's *Typography* table carries both new
                roles and says why neither widens this one.
              */}
              <p className="min-w-[7rem]">
                <span className={type.display}>{outstanding.total}</span>
                <span className={cn(type.metadata, 'mt-1 block')}>
                  {t('awaitingSubmission', { count: outstanding.total })}
                </span>
              </p>

              {/*
                §6.2: "Aging buckets as horizontal bars **with a height** … **6+ days renders in
                `negative`**." ⚠️ **That bullet read "hairline horizontal bars" until 2026-08-11 and this
                comment quoted it as the justification** — the rule was reversed by TASK-127 because at
                1 px a bar and a divider are the same object, and because a hairline spanning the width
                has nowhere to put its count but the far end of it. The `negative` half is unchanged.
                The bars are decoration — the band and its count are both words
                beside them (§3: colour never carries meaning alone) — so they are `aria-hidden`,
                and the oldest band carries the colour in its text as well as in its bar.

                The oldest band is the last one: the API sends the three in a fixed order and says
                so (`AgingBucket`: "always in this order, 0-2 → 3-5 → 6+"), which is a stabler
                handle than matching the label text it also chooses.
              */}
              <ul className="border-l border-rule pl-6">
                {outstanding.buckets.map((bucket, index) => {
                  const oldest = index === outstanding.buckets.length - 1;

                  return (
                    <li
                      key={bucket.label}
                      /*
                       * ⚠️ **The row reads label · bar · count, and the count's position is
                       * `re-decided`** (§6.2, 2026-08-12, from the reference; TASK-128's rule is
                       * reversed with its reasoning known). TASK-128 put the count immediately after
                       * its label for a measured reason: a bar spanning the column had left **797 px**
                       * between a label and the number describing it. The reference answers the same
                       * problem by a different route — it keeps the reading order and makes the **bar
                       * short**, a bounded 2.75rem track rather than a 12rem one — so what survives is
                       * the **distance, not the order**: label to count is ~116 px here against §6.2's
                       * bound of 120, and the live pass records the measured number.
                       */
                      className={cn(
                        'grid grid-cols-[3.5rem_minmax(0,2.75rem)_1.75rem] items-center gap-x-2 py-1',
                        oldest ? 'text-negative' : 'text-ink-500',
                      )}
                    >
                      <span className={cn(type.metadata, 'text-current')}>
                        {t('buckets.band', { label: bucket.label })}
                      </span>
                      {/*
                       * A bar with a height on a `paper-sunken` track — §6.2 called for a **hairline**
                       * until TASK-127 reversed it, and at 1 px a bar and a divider are the same
                       * object. ⚠️ **That half stands**: only the bar's *length* changed on 2026-08-12,
                       * and a bar and a divider must never be the same object. The track is what makes
                       * an empty band legible as zero rather than as a missing row; it is decoration
                       * either way, so the whole cell is `aria-hidden` and the band and its count are
                       * both words beside it (§3).
                       */}
                      <span className="h-2 rounded bg-paper-sunken" aria-hidden>
                        <span
                          className={cn(
                            'block h-2 rounded',
                            barWidth(bucket.count, largestBucket),
                            oldest ? 'bg-negative' : 'bg-ink-700',
                          )}
                        />
                      </span>
                      <span
                        className={cn(
                          type.numeral,
                          'text-right text-sm',
                          oldest ? 'text-negative' : 'text-ink-900',
                        )}
                      >
                        {bucket.count}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/*
              The oldest rows, in the hairline-bordered block the reference draws beside the bands.

              `interview_id` is the row's identity here **and its link target** — §6.2's *"rows link to
              the interview"*, landed by TASK-087 and moved with the primary column on 2026-08-12
              (`Outstanding/outstandingRow.tsx`, which is where the row is described **once** for this
              section and for the screen *View all →* leads to).

              ⚠️ **The header row is kept, and the decision is recorded rather than taken by accident**
              (TASK-151; the reference's own table shows none). It is §9's reason: the columns are named
              for every reader, and below `lg` `Table` repeats each column's name per cell from that same
              header. Hiding it would mean either editing `components/ui/Table.tsx` — shared by five
              screens and not this sprint's — or dropping the accessible column names with it.
            */}
            <div className="rounded border border-rule bg-paper">
              <Table
                className="border-y-0"
                columns={columns}
                rows={outstanding.oldest}
                rowKey={(row) => row.interview_id}
                empty={<EmptyState icon={CheckCircle2} message={t('nothingOutstanding')} />}
              />

              {/*
               * ⚠️ **The *View all →* the table has never had** (§6.2, TASK-151; the user asked for the
               * endpoint rather than for the affordance alone, 2026-08-12). The payload carries **five**
               * rows (`_OLDEST_LIMIT`) while the hero counts every one, so on the dev tenant three
               * outstanding interviews were unreachable from the one screen whose subject is outstanding
               * interviews.
               *
               * It renders whenever the table renders, truncated or not — a link that appears only past
               * a row count teaches the reader it is not there — and the zero state above has neither
               * table nor link. It is offered to **every** group that gets Section 1, an `Interviewer`
               * included: the destination carries their own outstanding work, narrowed by the server.
               *
               * The address comes from `OUTSTANDING_PATH` and is never spelled out here, so the link and
               * the mounted route cannot drift; `dashboardLinks.test.tsx` walks every href against
               * `buildRoutes()` and a link to an unmounted address falls through to the catch-all there.
               */}
              <p className="border-t border-rule px-3 py-2 text-right">
                <Link
                  to={OUTSTANDING_PATH}
                  aria-label={t('viewAllLabel')}
                  className="rounded text-sm text-ink-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {t('viewAll')} <span aria-hidden>→</span>
                </Link>
              </p>
            </div>
          </div>
        )}
      </section>

      {/*
       * ⚠️ **The order is Outstanding → Health → Pipeline → Distribution → Activity, and the
       * *Analysis* boundary between them is `reversed`** (§6.2, 2026-08-12, TASK-148/TASK-150; built
       * with TASK-127..129's reasoning known — it was put to the user and they chose the original
       * design). Until then an `Eyebrow` on a `rule-strong` hairline sat here carrying the period
       * selector, and Pipeline rendered **above** it because Sections 1 and 3 are current snapshots
       * while 2 and 4 are period-scoped (`security-auditor`, Sprint 18, MEDIUM).
       *
       * **Nothing in the layout divides the two scopes any more**, which is the trade this reversal
       * makes: the subtitle under the title states it in words for the whole screen, once, and that is
       * why it is load-bearing rather than decoration. Pipeline's own position is therefore free again,
       * and the reference puts Health second — the leak, then how fast the loop is closing, then the
       * state of the pipeline.
       */}
      {health && <HealthSection health={health} period={period} />}
      {pipeline && <PipelineSection pipeline={pipeline} />}
      {distribution && <FeedbackDistributionSection distribution={distribution} />}
      {activity && <InterviewerActivitySection activity={activity} />}
    </>
  );
}
